// Pavement, asphalt, grass and water textures.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { canvasTex } from '../lib/factory.js';

// clearcoated paint (a varnish layer over the colour), for the bike, the helmet and the cars

// Barcelona "panot de flor" sidewalk: 4x4 tiles of 0.6 units, each a four-petal flower pressed into the tile,
// shaded on one side like a relief; every tile has its own wear, a few are stained or newer, joints darker
export const panot = canvasTex(256, 256, (c) => {
  c.fillStyle = '#9f978a';
  c.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 4; i++)
    for (let j = 0; j < 4; j++) {
      const x0 = i * 64,
        y0 = j * 64,
        x = x0 + 32,
        y = y0 + 32,
        v = rr(-10, 10) + (rnd() < 0.08 ? 18 : 0) - (rnd() < 0.1 ? 14 : 0);
      const base = `rgb(${211 + v},${204 + v},${192 + v})`,
        dark = `rgba(120,110,95,.45)`,
        lit = 'rgba(255,252,240,.55)';
      c.fillStyle = base;
      c.fillRect(x0 + 1.5, y0 + 1.5, 61, 61);
      // petals: a lit rim on the top-left, a shadow on the bottom-right, then the recessed face
      for (const [ox, oy, col] of [
        [-1.2, -1.2, lit],
        [1.2, 1.2, dark],
        [0, 0, `rgb(${196 + v},${188 + v},${175 + v})`],
      ]) {
        c.fillStyle = col;
        for (let k = 0; k < 4; k++) {
          const a = (k * PI) / 2;
          c.beginPath();
          c.ellipse(x + Math.cos(a) * 12 + ox, y + Math.sin(a) * 12 + oy, 10, 7, a, 0, PI * 2);
          c.fill();
        }
      }
      c.fillStyle = base;
      c.beginPath();
      c.arc(x, y, 5, 0, PI * 2);
      c.fill();
      // quarter circles at the corners complete the neighbouring flowers' pattern
      c.fillStyle = `rgb(${198 + v},${190 + v},${178 + v})`;
      for (const [cx, cy] of [
        [x0, y0],
        [x0 + 64, y0],
        [x0, y0 + 64],
        [x0 + 64, y0 + 64],
      ]) {
        c.save();
        c.beginPath();
        c.rect(x0 + 1.5, y0 + 1.5, 61, 61);
        c.clip();
        c.beginPath();
        c.arc(cx, cy, 10, 0, PI * 2);
        c.fill();
        c.restore();
      }
      // wear: a soft grime cloud, a few specks, sometimes a stain
      const g = c.createRadialGradient(x + rr(-15, 15), y + rr(-15, 15), 0, x, y, 44);
      g.addColorStop(0, 'rgba(90,80,65,0)');
      g.addColorStop(1, `rgba(90,80,65,${rr(0.05, 0.16)})`);
      c.fillStyle = g;
      c.fillRect(x0 + 1.5, y0 + 1.5, 61, 61);
      for (let k = 0; k < 25; k++) {
        c.fillStyle = rnd() < 0.5 ? 'rgba(80,70,60,.25)' : 'rgba(255,255,250,.3)';
        c.fillRect(x0 + 2 + rnd() * 60, y0 + 2 + rnd() * 60, 1, 1);
      }
      if (rnd() < 0.12) {
        c.fillStyle = 'rgba(70,60,50,.18)';
        c.beginPath();
        c.ellipse(x + rr(-15, 15), y + rr(-15, 15), rr(6, 14), rr(4, 9), rnd() * PI, 0, PI * 2);
        c.fill();
      }
    }
});
panot.repeat.set(1 / 2.4, 1 / 2.4);
// worn city asphalt (10 units per tile): broad tonal drift, repair patches, oil drips, fine aggregate (no crack lines: the ink filter turns them into skid marks)
export const asphalt = canvasTex(512, 512, (c) => {
  c.fillStyle = '#77736d';
  c.fillRect(0, 0, 512, 512);
  for (let k = 0; k < 40; k++) {
    const x = rnd() * 512,
      y = rnd() * 512,
      r = rr(40, 120),
      g = c.createRadialGradient(x, y, 0, x, y, r),
      d = rnd() < 0.5;
    g.addColorStop(0, d ? 'rgba(70,68,64,.22)' : 'rgba(150,146,138,.16)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    c.fillStyle = g;
    c.fillRect(x - r, y - r, 2 * r, 2 * r);
  }
  // patches: a slightly darker, newer rectangle with a lighter worn seam
  for (let k = 0; k < 5; k++) {
    const x = rnd() * 440,
      y = rnd() * 440,
      w = rr(30, 90),
      h = rr(24, 70);
    c.fillStyle = 'rgba(62,60,58,.45)';
    c.fillRect(x, y, w, h);
    c.strokeStyle = 'rgba(150,146,140,.45)';
    c.lineWidth = 2;
    c.strokeRect(x, y, w, h);
  }
  for (let k = 0; k < 14000; k++) {
    const v = 95 + Math.floor(rnd() * 55);
    c.fillStyle = `rgba(${v},${v - 3},${v - 7},.8)`;
    c.fillRect(rnd() * 512, rnd() * 512, rr(1, 2.5), rr(1, 2.5));
  }
  for (let k = 0; k < 7; k++) {
    const x = rnd() * 512,
      y = rnd() * 512,
      r = rr(6, 18),
      g = c.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, 'rgba(35,33,32,.5)');
    g.addColorStop(1, 'rgba(35,33,32,0)');
    c.fillStyle = g;
    c.beginPath();
    c.ellipse(x, y, r, r * 0.7, rnd() * PI, 0, PI * 2);
    c.fill();
  }
});
asphalt.repeat.set(1 / 10, 1 / 10);
export const gravelTex = canvasTex(128, 128, (c) => {
  c.fillStyle = '#d8c39d';
  c.fillRect(0, 0, 128, 128);
  for (let k = 0; k < 3000; k++) {
    const v = rnd();
    c.fillStyle = v < 0.5 ? '#c9b48c' : v < 0.8 ? '#e6d4b2' : '#a8936d';
    c.fillRect(rnd() * 128, rnd() * 128, 1.5, 1.5);
  }
});
gravelTex.repeat.set(1 / 3, 1 / 3);
// painted lawn (BotW-like): broad sunlit and shaded patches, tufts drawn as little fans of strokes, clover clumps,
// a few daisies and buttercups; all marks wrap so the tile repeats seamlessly over 6 units
export const grassTex = canvasTex(256, 256, (c) => {
  c.fillStyle = '#78a94e';
  c.fillRect(0, 0, 256, 256);
  const wrap = (x, y, r, fn) => {
    for (const ox of [-256, 0, 256])
      for (const oy of [-256, 0, 256])
        if (x + ox + r > 0 && x + ox - r < 256 && y + oy + r > 0 && y + oy - r < 256)
          fn(x + ox, y + oy);
  };
  for (let k = 0; k < 22; k++) {
    const x0 = rnd() * 256,
      y0 = rnd() * 256,
      r = rr(30, 80),
      lit = rnd() < 0.5;
    wrap(x0, y0, r, (x, y) => {
      const g = c.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, lit ? 'rgba(178,214,110,.45)' : 'rgba(62,110,52,.35)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      c.fillStyle = g;
      c.fillRect(x - r, y - r, 2 * r, 2 * r);
    });
  }
  c.lineCap = 'round';
  for (let k = 0; k < 520; k++) {
    const x0 = rnd() * 256,
      y0 = rnd() * 256,
      n = 3 + Math.floor(rnd() * 3),
      h = rr(5, 10),
      col = pick(['#5f9440', '#6ea04a', '#8dbd5d', '#a6cf72', '#4f8236']);
    wrap(x0, y0, 12, (x, y) => {
      c.strokeStyle = col;
      c.lineWidth = rr(1, 1.8);
      for (let b = 0; b < n; b++) {
        const a = -PI / 2 + (b - (n - 1) / 2) * 0.32 + rr(-0.1, 0.1);
        c.beginPath();
        c.moveTo(x, y);
        c.quadraticCurveTo(
          x + Math.cos(a) * h * 0.5,
          y + Math.sin(a) * h * 0.6,
          x + Math.cos(a) * h,
          y + Math.sin(a) * h,
        );
        c.stroke();
      }
    });
  }
  for (let k = 0; k < 10; k++) {
    const x0 = rnd() * 256,
      y0 = rnd() * 256;
    wrap(x0, y0, 12, (x, y) => {
      for (let l = 0; l < 7; l++) {
        c.fillStyle = l % 2 ? '#5d9a45' : '#6fae52';
        c.beginPath();
        c.arc(x + rr(-7, 7), y + rr(-5, 5), rr(2, 3.2), 0, PI * 2);
        c.fill();
      }
    });
  }
  for (let k = 0; k < 26; k++) {
    const x0 = rnd() * 256,
      y0 = rnd() * 256,
      yellow = rnd() < 0.35;
    wrap(x0, y0, 4, (x, y) => {
      if (!yellow) {
        c.fillStyle = '#ffffff';
        for (let p = 0; p < 5; p++) {
          const a = (p * PI * 2) / 5;
          c.beginPath();
          c.arc(x + Math.cos(a) * 1.6, y + Math.sin(a) * 1.6, 1.1, 0, PI * 2);
          c.fill();
        }
      }
      c.fillStyle = yellow ? '#ffd23f' : '#f2c230';
      c.beginPath();
      c.arc(x, y, yellow ? 1.6 : 1, 0, PI * 2);
      c.fill();
    });
  }
});
grassTex.repeat.set(1 / 6, 1 / 6);
// normal map for water ripples, derived from a sum of sines (tileable)
export const rippleTex = (() => {
  const N = 128,
    c = document.createElement('canvas');
  c.width = c.height = N;
  const ctx = c.getContext('2d'),
    img = ctx.createImageData(N, N);
  const waves = [
    [1, 2, 0.3],
    [3, -1, 0.2],
    [-2, 3, 0.15],
    [5, 4, 0.08],
  ];
  for (let y = 0; y < N; y++)
    for (let x = 0; x < N; x++) {
      let dx = 0,
        dy = 0;
      for (const [kx, ky, a] of waves) {
        const ph = (2 * PI * (kx * x + ky * y)) / N,
          d = Math.cos(ph) * a * 2 * PI;
        dx += ((d * kx) / N) * 8;
        dy += ((d * ky) / N) * 8;
      }
      const n = new THREE.Vector3(-dx, -dy, 1).normalize(),
        i = (y * N + x) * 4;
      img.data[i] = (n.x * 0.5 + 0.5) * 255;
      img.data[i + 1] = (n.y * 0.5 + 0.5) * 255;
      img.data[i + 2] = (n.z * 0.5 + 0.5) * 255;
      img.data[i + 3] = 255;
    }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(0.25, 0.25);
  return t;
})();
